import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Plus } from 'lucide-react';
import { RootState, AppDispatch } from '../../redux/store';
import {
  fetchCategories,
  createCategory,
  updateCategory,
  deleteCategory,
  toggleCategory,
  setSelectedCategory
} from '../../redux/admin/adminSlice';
import { Category } from '../../types';
import { CategoryCreateRequest, CategoryUpdateRequest } from '../../services/api/categoryService';
import CategoryFormModal from './CategoryFormModal';
import {
  TaskyAlert,
  TaskyBadge,
  TaskyButton,
  TaskySpinner,
  TaskyTable,
  TaskyTableBody,
  TaskyTableCell,
  TaskyTableHead,
  TaskyTableHeaderCell,
  TaskyTableRow,
} from '../tasky';

const CategoryList: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { categories, isLoading, error } = useSelector((state: RootState) => state.admin);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState<Category | null>(null);
  const [deleteConfirmId, setDeleteConfirmId] = useState<number | null>(null);

  useEffect(() => {
    dispatch(fetchCategories());
  }, [dispatch]);

  const handleCreateCategory = () => {
    setEditingCategory(null);
    setIsModalOpen(true);
  };

  const handleEditCategory = (category: Category) => {
    setEditingCategory(category);
    setIsModalOpen(true);
  };

  const handleSubmitCategory = async (data: CategoryCreateRequest | CategoryUpdateRequest) => {
    try {
      if (editingCategory) {
        await dispatch(updateCategory({ id: editingCategory.id, categoryData: data })).unwrap();
      } else {
        await dispatch(createCategory(data)).unwrap();
      }
      setIsModalOpen(false);
      setEditingCategory(null);
    } catch (error) {
      console.error('カテゴリの保存に失敗しました:', error);
    }
  };

  const handleToggleCategory = async (categoryId: number) => {
    try {
      await dispatch(toggleCategory(categoryId)).unwrap();
    } catch (error) {
      console.error('カテゴリの状態変更に失敗しました:', error);
    }
  };

  const handleDeleteCategory = async (categoryId: number) => {
    try {
      await dispatch(deleteCategory(categoryId)).unwrap();
      setDeleteConfirmId(null);
    } catch (error) {
      console.error('カテゴリの削除に失敗しました:', error);
    }
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingCategory(null);
  };

  const sortedCategories = [...categories].sort((a, b) => {
    if (a.sort_order !== b.sort_order) {
      return a.sort_order - b.sort_order;
    }
    return a.name.localeCompare(b.name);
  });

  if (isLoading && categories.length === 0) {
    return (
      <div className="flex items-center justify-center py-16">
        <TaskySpinner size="lg" aria-label="読み込み中..." />
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-3">
          <h2 className="text-base font-semibold text-dark">カテゴリ一覧</h2>
          <span className="inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium bg-primary/10 text-primary">
            {sortedCategories.length}件
          </span>
        </div>
        <TaskyButton
          size="sm"
          onClick={handleCreateCategory}
          disabled={isLoading}
        >
          <Plus />
          新規カテゴリ追加
        </TaskyButton>
      </div>

      {error && (
        <TaskyAlert tone="error" className="mb-4">
          {error}
        </TaskyAlert>
      )}

      <TaskyTable>
        <TaskyTableHead>
          <tr>
            <TaskyTableHeaderCell>カテゴリ名</TaskyTableHeaderCell>
            <TaskyTableHeaderCell>説明</TaskyTableHeaderCell>
            <TaskyTableHeaderCell>カラー</TaskyTableHeaderCell>
            <TaskyTableHeaderCell>並び順</TaskyTableHeaderCell>
            <TaskyTableHeaderCell>状態</TaskyTableHeaderCell>
            <TaskyTableHeaderCell>使用数</TaskyTableHeaderCell>
            <TaskyTableHeaderCell>作成日</TaskyTableHeaderCell>
            <TaskyTableHeaderCell>操作</TaskyTableHeaderCell>
          </tr>
        </TaskyTableHead>
        <TaskyTableBody>
          {sortedCategories.length === 0 ? (
            <TaskyTableRow hoverable={false}>
              <TaskyTableCell colSpan={8} align="center" className="text-gray-500 py-8">
                カテゴリが登録されていません
              </TaskyTableCell>
            </TaskyTableRow>
          ) : (
            sortedCategories.map((category) => (
              <TaskyTableRow key={category.id}>
                <TaskyTableCell>
                  <div className="flex items-center gap-2 font-medium">
                    {category.color && (
                      <span
                        className="inline-block w-3 h-3 rounded-full shrink-0"
                        style={{ backgroundColor: category.color }}
                      />
                    )}
                    {category.name}
                  </div>
                </TaskyTableCell>
                <TaskyTableCell>
                  <div className="max-w-[280px] truncate text-gray-600">
                    {category.description || '-'}
                  </div>
                </TaskyTableCell>
                <TaskyTableCell>
                  <div className="flex items-center gap-2">
                    {category.color && (
                      <>
                        <span
                          className="inline-block w-5 h-5 rounded-tasky-xs border border-warm-border shrink-0"
                          style={{ backgroundColor: category.color }}
                        />
                        <span className="text-xs font-mono text-gray-500">{category.color}</span>
                      </>
                    )}
                  </div>
                </TaskyTableCell>
                <TaskyTableCell>{category.sort_order}</TaskyTableCell>
                <TaskyTableCell>
                  <TaskyBadge variant={category.is_active ? 'success' : 'secondary'}>
                    {category.is_active ? '有効' : '無効'}
                  </TaskyBadge>
                </TaskyTableCell>
                <TaskyTableCell>
                  <span className="inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium bg-primary/10 text-primary">
                    {category.ai_agents_count || 0}
                  </span>
                </TaskyTableCell>
                <TaskyTableCell className="text-gray-500">
                  {new Date(category.created_at).toLocaleDateString('ja-JP')}
                </TaskyTableCell>
                <TaskyTableCell>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      className="px-2.5 py-1 text-xs font-semibold bg-white border border-warm-border text-gray-700 rounded-tasky-xs hover:bg-warm-hover transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      onClick={() => handleEditCategory(category)}
                      disabled={isLoading}
                    >
                      編集
                    </button>
                    {deleteConfirmId === category.id ? (
                      <>
                        <button
                          type="button"
                          className="px-2.5 py-1 text-xs font-semibold bg-destructive border border-destructive text-white rounded-tasky-xs hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                          onClick={() => handleDeleteCategory(category.id)}
                          disabled={isLoading}
                        >
                          削除実行
                        </button>
                        <button
                          type="button"
                          className="px-2.5 py-1 text-xs font-semibold bg-white border border-warm-border text-gray-700 rounded-tasky-xs hover:bg-warm-hover transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                          onClick={() => setDeleteConfirmId(null)}
                          disabled={isLoading}
                        >
                          キャンセル
                        </button>
                      </>
                    ) : (
                      <button
                        type="button"
                        className="px-2.5 py-1 text-xs font-semibold bg-red-50 border border-red-200 text-red-600 rounded-tasky-xs hover:bg-red-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        onClick={() => setDeleteConfirmId(category.id)}
                        disabled={isLoading || (category.ai_agents_count || 0) > 0}
                        title={
                          (category.ai_agents_count || 0) > 0
                            ? 'このカテゴリは使用中のため削除できません'
                            : '削除'
                        }
                      >
                        削除
                      </button>
                    )}
                  </div>
                </TaskyTableCell>
              </TaskyTableRow>
            ))
          )}
        </TaskyTableBody>
      </TaskyTable>

      <CategoryFormModal
        isOpen={isModalOpen}
        category={editingCategory}
        onSubmit={handleSubmitCategory}
        onClose={handleCloseModal}
        isLoading={isLoading}
      />
    </div>
  );
};

export default CategoryList;
